import { useState } from 'react';
import { Modal, ModalFooter, ModalHeader, ModalBody, Form, Label, Button, FormFeedback, Spinner } from "reactstrap";
import { useForm, Controller } from 'react-hook-form'
import { Toaster, toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { useSelector } from "react-redux";

// Custom Component
import InputPasswordToggle from '../../components/input-password-toggle';

// Redux-Functions
import { selectLanguageUse } from '../../../redux/slices/language.Slice';

// Utility Service
import { getToken, getUser } from '../../../utility/AuthService';

// API Service
import { changePasswordService } from '../../../services/changePasswordService';
import { notificationQueueService } from '../../../services/notificationQueueService';
import { NotificationTypes } from '../../../services/notificationService';

const ChangePasswordModal = (props) => {
    const navigate = useNavigate()
    const token = getToken()
    const user = getUser()

    const languageUse = useSelector(selectLanguageUse || user?.language);

    const [isLoading, setLoading] = useState(false)

    const { control, handleSubmit, formState: { errors } } = useForm();

    /**
        * @function handleChangePassword
        * @param data
        * @description handle change password
    */
    const handleChangePassword = async (data) => {
        let result = []
        try {
            setLoading(true)
            result = await changePasswordService({
                authToken: token,
                password: data?.currentPassword,
                newPassword: data?.newPassword
            });

            if (result?.data?.statusCode === 200) {
                setLoading(false)
                
                // Send password change notification
                await notificationQueueService.addToQueue({
                    type: NotificationTypes.PASSWORD_CHANGED,
                    userId: user.id,
                    email: user.email,
                    data: {
                        username: user.username
                    },
                    priority: 'HIGH'
                });

                toast.success('Password Changed')
                navigate('/login')
            } else {
                setLoading(false)
                toast.error(result?.response?.data?.message || 'Something went wrong')
            }
        } catch (error) {
            setLoading(false)
            toast.error(result?.response?.data?.message || 'Something went wrong')
        }
    };

    /**
        * @function onSubmit
        * @param data
        * @description used to submit
    */
    const onSubmit = (data) => {
        if (data?.newPassword === data?.confirmPassword) {
            handleChangePassword(data)
        } else {
            toast.error(languageUse === 'English' ? 'New Password and Confirm Password must be same' : 'Le nouveau mot de passe et la confirmation du mot de passe doivent être identiques')
        }
    };

    return <>
        <div>
            <Toaster />
        </div>
        <div>
            <Modal
                isOpen={props.modal}
                toggle={props.toggle}
                backdropClassName='bg-dark'
            >
                <ModalHeader className='border-0'>{languageUse === 'English' ? 'Change Password' : 'Changer le mot de passe'}</ModalHeader>
                <Form className='auth-login-form mt-3' onSubmit={handleSubmit(onSubmit)}>
                    <ModalBody>
                        <div className='mb-4'>
                            <Label className='form-label' for='currentPassword'>
                                {languageUse === 'English' ? 'Current Password' : 'Mot de passe actuel'}
                            </Label>
                            <Controller
                                id='currentPassword'
                                name='currentPassword'
                                defaultValue=''
                                control={control}
                                render={({ field }) => (
                                    <InputPasswordToggle {...field} placeholder={languageUse === 'English' ? 'Current Password' : 'Mot de passe actuel'} invalid={errors.currentPassword && true} />
                                )}
                                rules={{
                                    required: languageUse === 'English' ? "Current Password is required" : "Le mot de passe actuel est requis"
                                }}
                            />
                            {errors.currentPassword && <FormFeedback>{errors.currentPassword.message}</FormFeedback>}
                        </div>

                        <div className='mb-4'>
                            <Label className='form-label' for='newPassword'>
                                {languageUse === 'English' ? 'New Password' : 'Nouveau mot de passe'}
                            </Label>
                            <Controller
                                id='newPassword'
                                name='newPassword'
                                defaultValue=''
                                control={control}
                                render={({ field }) => (
                                    <InputPasswordToggle {...field} placeholder={languageUse === 'English' ? 'New Password' : 'Nouveau mot de passe'} invalid={errors.newPassword && true} />
                                )}
                                rules={{
                                    required: languageUse === 'English' ? "New Password is required" : "Le nouveau mot de passe est requis"
                                }}
                            />
                            {errors.newPassword && <FormFeedback>{errors.newPassword.message}</FormFeedback>}
                        </div>

                        <div className='mb-3'>
                            <Label className='form-label' for='confirmPassword'>
                                {languageUse === 'English' ? 'Confirm New Password' : 'Confirmer le nouveau mot de passe'}
                            </Label>
                            <Controller
                                id='confirmPassword'
                                name='confirmPassword'
                                defaultValue=''
                                control={control}
                                render={({ field }) => (
                                    <InputPasswordToggle {...field} placeholder={languageUse === 'English' ? 'Confirm New Password' : 'Confirmer le nouveau mot de passe'} invalid={errors.confirmPassword && true} />
                                )}
                                rules={{
                                    required: languageUse === 'English' ? "Confirm New Password is required" : "La confirmation du nouveau mot de passe est requise"
                                }}
                            />
                            {errors.confirmPassword && <FormFeedback>{errors.confirmPassword.message}</FormFeedback>}
                        </div>
                    </ModalBody>
                    <ModalFooter className='d-flex justify-content-between'>
                        <Button className=" authButton login-btn-min-width mb-4" variant='outline-primary' onClick={props.toggle} >
                            {languageUse === 'English' ? 'Cancel' : 'Annuler'}
                        </Button>
                        <Button type='submit' className=" authButton login-btn-min-width mb-4" variant='outline-primary'>
                            {isLoading ? <Spinner variant='dark' size='sm' /> : languageUse === 'English' ? 'Save' : 'Enregistrer'}
                        </Button>
                    </ModalFooter>
                </Form>
            </Modal>
        </div>
    </>
};

export default ChangePasswordModal;